import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { cn } from "@/lib/utils";

// ────────────────────────────────────────────────────────────────────────────
//  Compact emoji data — 220+ commonly-used emojis, no external package
// ────────────────────────────────────────────────────────────────────────────

interface EmojiEntry {
    emoji: string;
    name: string;
    category: EmojiCategory;
    // tone variants index: 0=default,1=light,2=medium-light,3=medium,4=medium-dark,5=dark
    tones?: string[];
}

type EmojiCategory =
    | "smileys"
    | "animals"
    | "food"
    | "activities"
    | "travel"
    | "objects"
    | "symbols"
    | "flags";

const EMOJI_DATA: EmojiEntry[] = [
    // Smileys & People
    { emoji: "😀", name: "grinning face", category: "smileys" },
    { emoji: "😃", name: "grinning face with big eyes", category: "smileys" },
    { emoji: "😄", name: "grinning face with smiling eyes", category: "smileys" },
    { emoji: "😁", name: "beaming face with smiling eyes", category: "smileys" },
    { emoji: "😆", name: "grinning squinting face", category: "smileys" },
    { emoji: "😅", name: "grinning face with sweat", category: "smileys" },
    { emoji: "🤣", name: "rolling on the floor laughing", category: "smileys" },
    { emoji: "😂", name: "face with tears of joy", category: "smileys" },
    { emoji: "🙂", name: "slightly smiling face", category: "smileys" },
    { emoji: "🙃", name: "upside-down face", category: "smileys" },
    { emoji: "😉", name: "winking face", category: "smileys" },
    { emoji: "😊", name: "smiling face with smiling eyes", category: "smileys" },
    { emoji: "😇", name: "smiling face with halo", category: "smileys" },
    { emoji: "🥰", name: "smiling face with hearts", category: "smileys" },
    { emoji: "😍", name: "smiling face with heart-eyes", category: "smileys" },
    { emoji: "🤩", name: "star-struck", category: "smileys" },
    { emoji: "😘", name: "face blowing a kiss", category: "smileys" },
    { emoji: "😗", name: "kissing face", category: "smileys" },
    { emoji: "😚", name: "kissing face with closed eyes", category: "smileys" },
    { emoji: "😙", name: "kissing face with smiling eyes", category: "smileys" },
    { emoji: "🥲", name: "smiling face with tear", category: "smileys" },
    { emoji: "😋", name: "face savoring food", category: "smileys" },
    { emoji: "😛", name: "face with tongue", category: "smileys" },
    { emoji: "😜", name: "winking face with tongue", category: "smileys" },
    { emoji: "🤪", name: "zany face", category: "smileys" },
    { emoji: "😝", name: "squinting face with tongue", category: "smileys" },
    { emoji: "🤑", name: "money-mouth face", category: "smileys" },
    { emoji: "🤗", name: "smiling face with open hands", category: "smileys" },
    { emoji: "🤭", name: "face with hand over mouth", category: "smileys" },
    { emoji: "🤫", name: "shushing face", category: "smileys" },
    { emoji: "🤔", name: "thinking face", category: "smileys" },
    { emoji: "🤐", name: "zipper-mouth face", category: "smileys" },
    { emoji: "🤨", name: "face with raised eyebrow", category: "smileys" },
    { emoji: "😐", name: "neutral face", category: "smileys" },
    { emoji: "😑", name: "expressionless face", category: "smileys" },
    { emoji: "😶", name: "face without mouth", category: "smileys" },
    { emoji: "😏", name: "smirking face", category: "smileys" },
    { emoji: "😒", name: "unamused face", category: "smileys" },
    { emoji: "🙄", name: "face with rolling eyes", category: "smileys" },
    { emoji: "😬", name: "grimacing face", category: "smileys" },
    { emoji: "🤥", name: "lying face", category: "smileys" },
    { emoji: "😌", name: "relieved face", category: "smileys" },
    { emoji: "😔", name: "pensive face", category: "smileys" },
    { emoji: "😪", name: "sleepy face", category: "smileys" },
    { emoji: "🤤", name: "drooling face", category: "smileys" },
    { emoji: "😴", name: "sleeping face", category: "smileys" },
    { emoji: "😷", name: "face with medical mask", category: "smileys" },
    { emoji: "🤒", name: "face with thermometer", category: "smileys" },
    { emoji: "🤕", name: "face with head-bandage", category: "smileys" },
    { emoji: "🤢", name: "nauseated face", category: "smileys" },
    { emoji: "🤮", name: "face vomiting", category: "smileys" },
    { emoji: "🤧", name: "sneezing face", category: "smileys" },
    { emoji: "🥵", name: "hot face", category: "smileys" },
    { emoji: "🥶", name: "cold face", category: "smileys" },
    { emoji: "🥴", name: "woozy face", category: "smileys" },
    { emoji: "😵", name: "face with crossed-out eyes", category: "smileys" },
    { emoji: "🤯", name: "exploding head", category: "smileys" },
    { emoji: "🤠", name: "cowboy hat face", category: "smileys" },
    { emoji: "🥳", name: "partying face", category: "smileys" },
    { emoji: "😎", name: "smiling face with sunglasses", category: "smileys" },
    { emoji: "🤓", name: "nerd face", category: "smileys" },
    { emoji: "🧐", name: "face with monocle", category: "smileys" },
    { emoji: "😕", name: "confused face", category: "smileys" },
    { emoji: "😟", name: "worried face", category: "smileys" },
    { emoji: "🙁", name: "slightly frowning face", category: "smileys" },
    { emoji: "☹️", name: "frowning face", category: "smileys" },
    { emoji: "😮", name: "face with open mouth", category: "smileys" },
    { emoji: "😯", name: "hushed face", category: "smileys" },
    { emoji: "😲", name: "astonished face", category: "smileys" },
    { emoji: "😳", name: "flushed face", category: "smileys" },
    { emoji: "🥺", name: "pleading face", category: "smileys" },
    { emoji: "😦", name: "frowning face with open mouth", category: "smileys" },
    { emoji: "😧", name: "anguished face", category: "smileys" },
    { emoji: "😨", name: "fearful face", category: "smileys" },
    { emoji: "😰", name: "anxious face with sweat", category: "smileys" },
    { emoji: "😥", name: "sad but relieved face", category: "smileys" },
    { emoji: "😢", name: "crying face", category: "smileys" },
    { emoji: "😭", name: "loudly crying face", category: "smileys" },
    { emoji: "😱", name: "face screaming in fear", category: "smileys" },
    { emoji: "😖", name: "confounded face", category: "smileys" },
    { emoji: "😣", name: "persevering face", category: "smileys" },
    { emoji: "😞", name: "disappointed face", category: "smileys" },
    { emoji: "😓", name: "downcast face with sweat", category: "smileys" },
    { emoji: "😩", name: "weary face", category: "smileys" },
    { emoji: "😫", name: "tired face", category: "smileys" },
    { emoji: "🥱", name: "yawning face", category: "smileys" },
    { emoji: "😤", name: "face with steam from nose", category: "smileys" },
    { emoji: "😡", name: "enraged face", category: "smileys" },
    { emoji: "😠", name: "angry face", category: "smileys" },
    { emoji: "🤬", name: "face with symbols on mouth", category: "smileys" },
    { emoji: "😈", name: "smiling face with horns", category: "smileys" },
    { emoji: "👿", name: "angry face with horns", category: "smileys" },
    { emoji: "💀", name: "skull", category: "smileys" },
    { emoji: "💩", name: "pile of poo", category: "smileys" },
    { emoji: "🤡", name: "clown face", category: "smileys" },
    { emoji: "👹", name: "ogre", category: "smileys" },
    { emoji: "👺", name: "goblin", category: "smileys" },
    { emoji: "👻", name: "ghost", category: "smileys" },
    { emoji: "👽", name: "alien", category: "smileys" },
    { emoji: "🤖", name: "robot", category: "smileys" },
    { emoji: "👋", name: "waving hand", category: "smileys", tones: ["👋🏻", "👋🏼", "👋🏽", "👋🏾", "👋🏿"] },
    { emoji: "🤚", name: "raised back of hand", category: "smileys", tones: ["🤚🏻", "🤚🏼", "🤚🏽", "🤚🏾", "🤚🏿"] },
    { emoji: "🖐️", name: "hand with fingers splayed", category: "smileys" },
    { emoji: "✋", name: "raised hand", category: "smileys", tones: ["✋🏻", "✋🏼", "✋🏽", "✋🏾", "✋🏿"] },
    { emoji: "🖖", name: "vulcan salute", category: "smileys" },
    { emoji: "👌", name: "ok hand", category: "smileys", tones: ["👌🏻", "👌🏼", "👌🏽", "👌🏾", "👌🏿"] },
    { emoji: "🤌", name: "pinched fingers", category: "smileys" },
    { emoji: "✌️", name: "victory hand", category: "smileys" },
    { emoji: "🤞", name: "crossed fingers", category: "smileys", tones: ["🤞🏻", "🤞🏼", "🤞🏽", "🤞🏾", "🤞🏿"] },
    { emoji: "🤟", name: "love-you gesture", category: "smileys" },
    { emoji: "🤘", name: "sign of the horns", category: "smileys" },
    { emoji: "👈", name: "backhand index pointing left", category: "smileys" },
    { emoji: "👉", name: "backhand index pointing right", category: "smileys" },
    { emoji: "👆", name: "backhand index pointing up", category: "smileys" },
    { emoji: "👇", name: "backhand index pointing down", category: "smileys" },
    { emoji: "☝️", name: "index pointing up", category: "smileys" },
    { emoji: "👍", name: "thumbs up", category: "smileys", tones: ["👍🏻", "👍🏼", "👍🏽", "👍🏾", "👍🏿"] },
    { emoji: "👎", name: "thumbs down", category: "smileys", tones: ["👎🏻", "👎🏼", "👎🏽", "👎🏾", "👎🏿"] },
    { emoji: "✊", name: "raised fist", category: "smileys" },
    { emoji: "👊", name: "oncoming fist", category: "smileys" },
    { emoji: "🤛", name: "left-facing fist", category: "smileys" },
    { emoji: "🤜", name: "right-facing fist", category: "smileys" },
    { emoji: "👏", name: "clapping hands", category: "smileys", tones: ["👏🏻", "👏🏼", "👏🏽", "👏🏾", "👏🏿"] },
    { emoji: "🙌", name: "raising hands", category: "smileys" },
    { emoji: "👐", name: "open hands", category: "smileys" },
    { emoji: "🤲", name: "palms up together", category: "smileys" },
    { emoji: "🙏", name: "folded hands", category: "smileys", tones: ["🙏🏻", "🙏🏼", "🙏🏽", "🙏🏾", "🙏🏿"] },
    { emoji: "✍️", name: "writing hand", category: "smileys" },
    { emoji: "💅", name: "nail polish", category: "smileys" },
    { emoji: "🤳", name: "selfie", category: "smileys" },
    { emoji: "💪", name: "flexed biceps", category: "smileys", tones: ["💪🏻", "💪🏼", "💪🏽", "💪🏾", "💪🏿"] },
    { emoji: "🦾", name: "mechanical arm", category: "smileys" },
    { emoji: "❤️", name: "red heart", category: "smileys" },
    { emoji: "🧡", name: "orange heart", category: "smileys" },
    { emoji: "💛", name: "yellow heart", category: "smileys" },
    { emoji: "💚", name: "green heart", category: "smileys" },
    { emoji: "💙", name: "blue heart", category: "smileys" },
    { emoji: "💜", name: "purple heart", category: "smileys" },
    { emoji: "🖤", name: "black heart", category: "smileys" },
    { emoji: "🤍", name: "white heart", category: "smileys" },
    { emoji: "🤎", name: "brown heart", category: "smileys" },
    { emoji: "💔", name: "broken heart", category: "smileys" },
    { emoji: "❣️", name: "heart exclamation", category: "smileys" },
    { emoji: "💕", name: "two hearts", category: "smileys" },
    { emoji: "💞", name: "revolving hearts", category: "smileys" },
    { emoji: "💓", name: "beating heart", category: "smileys" },
    { emoji: "💗", name: "growing heart", category: "smileys" },
    { emoji: "💖", name: "sparkling heart", category: "smileys" },
    { emoji: "💘", name: "heart with arrow", category: "smileys" },
    { emoji: "💝", name: "heart with ribbon", category: "smileys" },
    { emoji: "💟", name: "heart decoration", category: "smileys" },
    // Animals & Nature
    { emoji: "🐶", name: "dog face", category: "animals" },
    { emoji: "🐱", name: "cat face", category: "animals" },
    { emoji: "🐭", name: "mouse face", category: "animals" },
    { emoji: "🐹", name: "hamster", category: "animals" },
    { emoji: "🐰", name: "rabbit face", category: "animals" },
    { emoji: "🦊", name: "fox", category: "animals" },
    { emoji: "🐻", name: "bear", category: "animals" },
    { emoji: "🐼", name: "panda", category: "animals" },
    { emoji: "🐨", name: "koala", category: "animals" },
    { emoji: "🐯", name: "tiger face", category: "animals" },
    { emoji: "🦁", name: "lion", category: "animals" },
    { emoji: "🐮", name: "cow face", category: "animals" },
    { emoji: "🐷", name: "pig face", category: "animals" },
    { emoji: "🐸", name: "frog", category: "animals" },
    { emoji: "🐵", name: "monkey face", category: "animals" },
    { emoji: "🙈", name: "see-no-evil monkey", category: "animals" },
    { emoji: "🙉", name: "hear-no-evil monkey", category: "animals" },
    { emoji: "🙊", name: "speak-no-evil monkey", category: "animals" },
    { emoji: "🐔", name: "chicken", category: "animals" },
    { emoji: "🐧", name: "penguin", category: "animals" },
    { emoji: "🐦", name: "bird", category: "animals" },
    { emoji: "🦆", name: "duck", category: "animals" },
    { emoji: "🦅", name: "eagle", category: "animals" },
    { emoji: "🦉", name: "owl", category: "animals" },
    { emoji: "🐺", name: "wolf", category: "animals" },
    { emoji: "🐗", name: "boar", category: "animals" },
    { emoji: "🐴", name: "horse face", category: "animals" },
    { emoji: "🦄", name: "unicorn", category: "animals" },
    { emoji: "🦋", name: "butterfly", category: "animals" },
    { emoji: "🐛", name: "bug", category: "animals" },
    { emoji: "🐝", name: "honeybee", category: "animals" },
    { emoji: "🐌", name: "snail", category: "animals" },
    { emoji: "🐢", name: "turtle", category: "animals" },
    { emoji: "🐍", name: "snake", category: "animals" },
    { emoji: "🦕", name: "sauropod", category: "animals" },
    { emoji: "🦖", name: "T-rex", category: "animals" },
    { emoji: "🐳", name: "spouting whale", category: "animals" },
    { emoji: "🐬", name: "dolphin", category: "animals" },
    { emoji: "🦈", name: "shark", category: "animals" },
    { emoji: "🐙", name: "octopus", category: "animals" },
    { emoji: "🦀", name: "crab", category: "animals" },
    { emoji: "🌸", name: "cherry blossom", category: "animals" },
    { emoji: "🌺", name: "hibiscus", category: "animals" },
    { emoji: "🌻", name: "sunflower", category: "animals" },
    { emoji: "🌹", name: "rose", category: "animals" },
    { emoji: "🌷", name: "tulip", category: "animals" },
    { emoji: "🌱", name: "seedling", category: "animals" },
    { emoji: "🌲", name: "evergreen tree", category: "animals" },
    { emoji: "🍀", name: "four leaf clover", category: "animals" },
    { emoji: "🍁", name: "maple leaf", category: "animals" },
    { emoji: "🌊", name: "water wave", category: "animals" },
    { emoji: "🌈", name: "rainbow", category: "animals" },
    { emoji: "⭐", name: "star", category: "animals" },
    { emoji: "🌙", name: "crescent moon", category: "animals" },
    { emoji: "☀️", name: "sun", category: "animals" },
    { emoji: "⛅", name: "partly cloudy", category: "animals" },
    { emoji: "🌧️", name: "rain", category: "animals" },
    { emoji: "❄️", name: "snowflake", category: "animals" },
    { emoji: "🔥", name: "fire", category: "animals" },
    { emoji: "💧", name: "droplet", category: "animals" },
    // Food & Drink
    { emoji: "🍕", name: "pizza", category: "food" },
    { emoji: "🍔", name: "hamburger", category: "food" },
    { emoji: "🌮", name: "taco", category: "food" },
    { emoji: "🌯", name: "burrito", category: "food" },
    { emoji: "🥙", name: "stuffed flatbread", category: "food" },
    { emoji: "🍜", name: "steaming bowl", category: "food" },
    { emoji: "🍝", name: "spaghetti", category: "food" },
    { emoji: "🍛", name: "curry rice", category: "food" },
    { emoji: "🍣", name: "sushi", category: "food" },
    { emoji: "🍱", name: "bento box", category: "food" },
    { emoji: "🥗", name: "green salad", category: "food" },
    { emoji: "🍰", name: "shortcake", category: "food" },
    { emoji: "🎂", name: "birthday cake", category: "food" },
    { emoji: "🍩", name: "doughnut", category: "food" },
    { emoji: "🍪", name: "cookie", category: "food" },
    { emoji: "🍫", name: "chocolate bar", category: "food" },
    { emoji: "🍬", name: "candy", category: "food" },
    { emoji: "🍭", name: "lollipop", category: "food" },
    { emoji: "🍦", name: "soft ice cream", category: "food" },
    { emoji: "🍓", name: "strawberry", category: "food" },
    { emoji: "🍎", name: "red apple", category: "food" },
    { emoji: "🍊", name: "tangerine", category: "food" },
    { emoji: "🍋", name: "lemon", category: "food" },
    { emoji: "🍇", name: "grapes", category: "food" },
    { emoji: "🍉", name: "watermelon", category: "food" },
    { emoji: "🥑", name: "avocado", category: "food" },
    { emoji: "🌽", name: "ear of corn", category: "food" },
    { emoji: "☕", name: "hot beverage", category: "food" },
    { emoji: "🍵", name: "teacup without handle", category: "food" },
    { emoji: "🧋", name: "bubble tea", category: "food" },
    { emoji: "🍺", name: "beer mug", category: "food" },
    { emoji: "🥂", name: "clinking glasses", category: "food" },
    { emoji: "🍾", name: "bottle with popping cork", category: "food" },
    // Activities
    { emoji: "⚽", name: "soccer ball", category: "activities" },
    { emoji: "🏀", name: "basketball", category: "activities" },
    { emoji: "🏈", name: "american football", category: "activities" },
    { emoji: "⚾", name: "baseball", category: "activities" },
    { emoji: "🎾", name: "tennis", category: "activities" },
    { emoji: "🏐", name: "volleyball", category: "activities" },
    { emoji: "🏓", name: "ping pong", category: "activities" },
    { emoji: "🎱", name: "pool 8 ball", category: "activities" },
    { emoji: "🏊", name: "person swimming", category: "activities" },
    { emoji: "🚴", name: "person biking", category: "activities" },
    { emoji: "🏋️", name: "person lifting weights", category: "activities" },
    { emoji: "🧘", name: "person in lotus position", category: "activities" },
    { emoji: "🎮", name: "video game", category: "activities" },
    { emoji: "🎲", name: "game die", category: "activities" },
    { emoji: "🎯", name: "direct hit", category: "activities" },
    { emoji: "🎳", name: "bowling", category: "activities" },
    { emoji: "🎭", name: "performing arts", category: "activities" },
    { emoji: "🎨", name: "artist palette", category: "activities" },
    { emoji: "🎤", name: "microphone", category: "activities" },
    { emoji: "🎵", name: "musical note", category: "activities" },
    { emoji: "🎶", name: "musical notes", category: "activities" },
    { emoji: "🎉", name: "party popper", category: "activities" },
    { emoji: "🎊", name: "confetti ball", category: "activities" },
    { emoji: "🏆", name: "trophy", category: "activities" },
    { emoji: "🥇", name: "1st place medal", category: "activities" },
    { emoji: "🎗️", name: "reminder ribbon", category: "activities" },
    // Travel & Places
    { emoji: "🚀", name: "rocket", category: "travel" },
    { emoji: "✈️", name: "airplane", category: "travel" },
    { emoji: "🚂", name: "locomotive", category: "travel" },
    { emoji: "🚗", name: "automobile", category: "travel" },
    { emoji: "🚕", name: "taxi", category: "travel" },
    { emoji: "🚌", name: "bus", category: "travel" },
    { emoji: "🏠", name: "house", category: "travel" },
    { emoji: "🏢", name: "office building", category: "travel" },
    { emoji: "🏖️", name: "beach with umbrella", category: "travel" },
    { emoji: "🏔️", name: "snow-capped mountain", category: "travel" },
    { emoji: "🗺️", name: "world map", category: "travel" },
    { emoji: "🗼", name: "tokyo tower", category: "travel" },
    { emoji: "🗽", name: "statue of liberty", category: "travel" },
    { emoji: "🌍", name: "globe europe-africa", category: "travel" },
    { emoji: "🌎", name: "globe americas", category: "travel" },
    { emoji: "🌏", name: "globe asia-australia", category: "travel" },
    // Objects
    { emoji: "📱", name: "mobile phone", category: "objects" },
    { emoji: "💻", name: "laptop", category: "objects" },
    { emoji: "🖥️", name: "desktop computer", category: "objects" },
    { emoji: "⌨️", name: "keyboard", category: "objects" },
    { emoji: "🖨️", name: "printer", category: "objects" },
    { emoji: "📷", name: "camera", category: "objects" },
    { emoji: "📺", name: "television", category: "objects" },
    { emoji: "📻", name: "radio", category: "objects" },
    { emoji: "🎧", name: "headphone", category: "objects" },
    { emoji: "🔋", name: "battery", category: "objects" },
    { emoji: "💡", name: "light bulb", category: "objects" },
    { emoji: "🔦", name: "flashlight", category: "objects" },
    { emoji: "🕯️", name: "candle", category: "objects" },
    { emoji: "📚", name: "books", category: "objects" },
    { emoji: "📖", name: "open book", category: "objects" },
    { emoji: "📝", name: "memo", category: "objects" },
    { emoji: "✏️", name: "pencil", category: "objects" },
    { emoji: "🖊️", name: "pen", category: "objects" },
    { emoji: "📎", name: "paperclip", category: "objects" },
    { emoji: "🔍", name: "magnifying glass tilted left", category: "objects" },
    { emoji: "🔑", name: "key", category: "objects" },
    { emoji: "🔒", name: "locked", category: "objects" },
    { emoji: "🔓", name: "unlocked", category: "objects" },
    { emoji: "🔧", name: "wrench", category: "objects" },
    { emoji: "⚙️", name: "gear", category: "objects" },
    { emoji: "💊", name: "pill", category: "objects" },
    { emoji: "🩺", name: "stethoscope", category: "objects" },
    { emoji: "📦", name: "package", category: "objects" },
    { emoji: "✉️", name: "envelope", category: "objects" },
    { emoji: "📧", name: "e-mail", category: "objects" },
    { emoji: "💬", name: "speech balloon", category: "objects" },
    { emoji: "💭", name: "thought balloon", category: "objects" },
    // Symbols
    { emoji: "✅", name: "check mark button", category: "symbols" },
    { emoji: "❌", name: "cross mark", category: "symbols" },
    { emoji: "⚠️", name: "warning", category: "symbols" },
    { emoji: "🚫", name: "prohibited", category: "symbols" },
    { emoji: "♻️", name: "recycling symbol", category: "symbols" },
    { emoji: "✔️", name: "check mark", category: "symbols" },
    { emoji: "❓", name: "question mark", category: "symbols" },
    { emoji: "❗", name: "exclamation mark", category: "symbols" },
    { emoji: "💯", name: "hundred points", category: "symbols" },
    { emoji: "🔴", name: "red circle", category: "symbols" },
    { emoji: "🟡", name: "yellow circle", category: "symbols" },
    { emoji: "🟢", name: "green circle", category: "symbols" },
    { emoji: "🔵", name: "blue circle", category: "symbols" },
    { emoji: "⚡", name: "high voltage", category: "symbols" },
    { emoji: "💫", name: "dizzy", category: "symbols" },
    { emoji: "✨", name: "sparkles", category: "symbols" },
    { emoji: "🎯", name: "bullseye", category: "symbols" },
    { emoji: "🏁", name: "chequered flag", category: "symbols" },
    { emoji: "🔔", name: "bell", category: "symbols" },
    { emoji: "🔕", name: "bell with slash", category: "symbols" },
    { emoji: "🔇", name: "muted speaker", category: "symbols" },
    { emoji: "🔊", name: "speaker high volume", category: "symbols" },
    { emoji: "⬆️", name: "up arrow", category: "symbols" },
    { emoji: "⬇️", name: "down arrow", category: "symbols" },
    { emoji: "➡️", name: "right arrow", category: "symbols" },
    { emoji: "⬅️", name: "left arrow", category: "symbols" },
    { emoji: "🔄", name: "counterclockwise arrows", category: "symbols" },
    { emoji: "🔃", name: "clockwise arrows", category: "symbols" },
    // Flags
    { emoji: "🏳️", name: "white flag", category: "flags" },
    { emoji: "🏴", name: "black flag", category: "flags" },
    { emoji: "🚩", name: "triangular flag", category: "flags" },
    { emoji: "🏁", name: "chequered flag", category: "flags" },
    { emoji: "🎌", name: "crossed flags", category: "flags" },
    { emoji: "🏳️‍🌈", name: "rainbow flag", category: "flags" },
    { emoji: "🏳️‍⚧️", name: "transgender flag", category: "flags" },
];

const CATEGORY_LABELS: Record<EmojiCategory, string> = {
    smileys: "Smileys & People",
    animals: "Animals & Nature",
    food: "Food & Drink",
    activities: "Activities",
    travel: "Travel & Places",
    objects: "Objects",
    symbols: "Symbols",
    flags: "Flags",
};

const CATEGORY_ORDER: EmojiCategory[] = [
    "smileys", "animals", "food", "activities", "travel", "objects", "symbols", "flags"
];

const SKIN_TONE_LABELS = ["Default", "Light", "Medium-Light", "Medium", "Medium-Dark", "Dark"];
const SKIN_TONE_SWATCHES = ["🟡", "🏻", "🏼", "🏽", "🏾", "🏿"];

const RECENT_KEY = "athavita:connect:recent-emoji";
const SKIN_TONE_KEY = "athavita:connect:skin-tone";
const MAX_RECENT = 24;

function loadRecent(): string[] {
    try { return JSON.parse(localStorage.getItem(RECENT_KEY) ?? "[]") as string[]; }
    catch { return []; }
}
function saveRecent(list: string[]) {
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, MAX_RECENT))); }
    catch { /* ignore quota errors */ }
}
function loadSkinTone(): number {
    try { return Number(localStorage.getItem(SKIN_TONE_KEY) ?? "0"); }
    catch { return 0; }
}
function saveSkinTone(tone: number) {
    try { localStorage.setItem(SKIN_TONE_KEY, String(tone)); }
    catch { /* ignore */ }
}

interface EmojiPickerProps {
    onSelect: (emoji: string) => void;
    onClose: () => void;
}

/**
 * Full emoji picker with category tabs, search, skin-tone modifier,
 * recently-used section, and full keyboard navigation.
 *
 * Sends a `connect:insert-emoji` CustomEvent on the document for the
 * MentionTextarea to intercept; also calls `onSelect` for callers that
 * own their own insertion logic.
 */
export function EmojiPicker({ onSelect, onClose }: EmojiPickerProps) {
    const [query, setQuery] = useState("");
    const [activeCategory, setActiveCategory] = useState<EmojiCategory | "recent">("smileys");
    const [skinTone, setSkinTone] = useState<number>(() => loadSkinTone());
    const [recent, setRecent] = useState<string[]>(() => loadRecent());
    const [focusIdx, setFocusIdx] = useState(0);
    const [showTonePicker, setShowTonePicker] = useState(false);
    const searchRef = useRef<HTMLInputElement | null>(null);
    const gridRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        searchRef.current?.focus();
    }, []);

    // Close on Escape
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") { e.stopPropagation(); onClose(); }
        };
        window.addEventListener("keydown", onKey, true);
        return () => window.removeEventListener("keydown", onKey, true);
    }, [onClose]);

    const applyTone = useCallback((entry: EmojiEntry, tone: number): string => {
        if (tone === 0 || !entry.tones) return entry.emoji;
        return entry.tones[tone - 1] ?? entry.emoji;
    }, []);

    const visibleEmojis = useMemo<string[]>(() => {
        if (query.length >= 1) {
            const q = query.toLowerCase();
            return EMOJI_DATA
                .filter((e) => e.name.includes(q) || e.emoji === q)
                .map((e) => applyTone(e, skinTone));
        }
        if (activeCategory === "recent") return recent;
        return EMOJI_DATA
            .filter((e) => e.category === activeCategory)
            .map((e) => applyTone(e, skinTone));
    }, [query, activeCategory, skinTone, recent, applyTone]);

    const pickEmoji = useCallback((emoji: string) => {
        // Update recently used
        const next = [emoji, ...recent.filter((e) => e !== emoji)].slice(0, MAX_RECENT);
        setRecent(next);
        saveRecent(next);
        onSelect(emoji);
        // Fire custom event for MentionTextarea
        document.dispatchEvent(new CustomEvent("connect:insert-emoji", { detail: { emoji } }));
        onClose();
    }, [onSelect, onClose, recent]);

    // Keyboard navigation in grid
    const onGridKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
        const cols = 9;
        const len = visibleEmojis.length;
        if (len === 0) return;
        if (e.key === "ArrowRight") { e.preventDefault(); setFocusIdx((i) => Math.min(i + 1, len - 1)); }
        if (e.key === "ArrowLeft")  { e.preventDefault(); setFocusIdx((i) => Math.max(i - 1, 0)); }
        if (e.key === "ArrowDown")  { e.preventDefault(); setFocusIdx((i) => Math.min(i + cols, len - 1)); }
        if (e.key === "ArrowUp")    { e.preventDefault(); setFocusIdx((i) => Math.max(i - cols, 0)); }
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            const em = visibleEmojis[focusIdx];
            if (em) pickEmoji(em);
        }
    }, [visibleEmojis, focusIdx, pickEmoji]);

    // Reset focus when list changes
    useEffect(() => { setFocusIdx(0); }, [visibleEmojis]);

    // Focus the focused cell when idx changes
    useEffect(() => {
        const grid = gridRef.current;
        if (!grid) return;
        const btn = grid.querySelectorAll<HTMLButtonElement>("button.emoji-cell")[focusIdx];
        btn?.focus();
    }, [focusIdx]);

    const changeSkinTone = (tone: number) => {
        setSkinTone(tone);
        saveSkinTone(tone);
        setShowTonePicker(false);
    };

    return (
        <div
            className="z-50 flex w-80 flex-col rounded-lg border bg-card shadow-xl"
            role="dialog"
            aria-label="Emoji picker"
            onClick={(e) => e.stopPropagation()}
        >
            {/* Search bar */}
            <div className="flex items-center gap-2 border-b px-2 py-2">
                <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
                <input
                    ref={searchRef}
                    type="search"
                    placeholder="Search emoji…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                    aria-label="Search emoji"
                />
                {query ? (
                    <button type="button" onClick={() => setQuery("")} className="rounded p-0.5 hover:bg-muted" aria-label="Clear search">
                        <X className="h-3.5 w-3.5 text-muted-foreground" />
                    </button>
                ) : null}
            </div>

            {/* Category tabs */}
            {!query ? (
                <div className="flex overflow-x-auto border-b" role="tablist" aria-label="Emoji categories">
                    {([["recent", "🕐", "Recently used"]] as [string, string, string][])
                        .concat(CATEGORY_ORDER.map((c) => [c, categoryIcon(c), CATEGORY_LABELS[c]]))
                        .map(([id, icon, label]) => (
                            <button
                                key={id}
                                type="button"
                                role="tab"
                                aria-selected={activeCategory === id}
                                onClick={() => setActiveCategory(id as EmojiCategory | "recent")}
                                title={label}
                                className={cn(
                                    "shrink-0 px-2 py-1.5 text-base hover:bg-muted",
                                    activeCategory === id && "border-b-2 border-primary bg-primary/5"
                                )}
                            >
                                {icon}
                            </button>
                        ))}
                </div>
            ) : null}

            {/* Emoji grid */}
            <div
                ref={gridRef}
                className="max-h-52 overflow-y-auto p-1"
                onKeyDown={onGridKeyDown}
                aria-label={query ? `Emoji search results for "${query}"` : CATEGORY_LABELS[activeCategory as EmojiCategory] ?? "Recently used"}
            >
                {visibleEmojis.length === 0 ? (
                    <p className="py-6 text-center text-xs text-muted-foreground">
                        {query ? "No emoji found" : "No recent emoji yet"}
                    </p>
                ) : (
                    <div className="grid grid-cols-9 gap-0.5">
                        {visibleEmojis.map((emoji, idx) => (
                            <button
                                key={`${emoji}-${idx}`}
                                type="button"
                                className="emoji-cell flex h-8 w-8 items-center justify-center rounded text-lg hover:bg-muted focus:bg-accent focus:outline-none"
                                aria-label={emoji}
                                tabIndex={idx === focusIdx ? 0 : -1}
                                onMouseEnter={() => setFocusIdx(idx)}
                                onClick={() => pickEmoji(emoji)}
                            >
                                {emoji}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Footer: skin tone picker */}
            <div className="flex items-center justify-between border-t px-2 py-1.5">
                <span className="text-[10px] text-muted-foreground">
                    {query ? `${visibleEmojis.length} result${visibleEmojis.length !== 1 ? "s" : ""}` : CATEGORY_LABELS[activeCategory as EmojiCategory] ?? "Recent"}
                </span>
                <div className="relative">
                    <button
                        type="button"
                        onClick={() => setShowTonePicker((v) => !v)}
                        title={`Skin tone: ${SKIN_TONE_LABELS[skinTone]}`}
                        className="rounded px-1.5 py-0.5 text-base hover:bg-muted"
                        aria-label="Choose skin tone"
                    >
                        {SKIN_TONE_SWATCHES[skinTone]}
                    </button>
                    {showTonePicker ? (
                        <div className="absolute bottom-full right-0 mb-1 flex gap-0.5 rounded-md border bg-popover p-1 shadow-md">
                            {SKIN_TONE_SWATCHES.map((swatch, i) => (
                                <button
                                    key={i}
                                    type="button"
                                    onClick={() => changeSkinTone(i)}
                                    title={SKIN_TONE_LABELS[i]}
                                    aria-pressed={skinTone === i}
                                    className={cn(
                                        "rounded px-1 py-0.5 text-base hover:bg-muted",
                                        skinTone === i && "ring-2 ring-primary"
                                    )}
                                >
                                    {swatch}
                                </button>
                            ))}
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

function categoryIcon(category: EmojiCategory): string {
    const icons: Record<EmojiCategory, string> = {
        smileys: "😊",
        animals: "🐶",
        food: "🍕",
        activities: "⚽",
        travel: "🚀",
        objects: "💡",
        symbols: "✅",
        flags: "🏳️",
    };
    return icons[category];
}

// ────────────────────────────────────────────────────────────────────────────
//  Trigger button + popover wrapper (for use in Composer)
// ────────────────────────────────────────────────────────────────────────────

interface EmojiPickerTriggerProps {
    onSelect: (emoji: string) => void;
    /** className for the trigger button */
    className?: string;
}

export function EmojiPickerTrigger({ onSelect, className }: EmojiPickerTriggerProps) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement | null>(null);

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [open]);

    return (
        <div ref={ref} className="relative">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-label="Emoji picker"
                aria-expanded={open}
                title="Emoji"
                className={cn(
                    "flex h-9 w-9 items-center justify-center rounded-md border hover:bg-muted",
                    className
                )}
            >
                <span aria-hidden className="text-base">😊</span>
            </button>
            {open ? (
                <div className="absolute bottom-full left-0 mb-2 z-50">
                    <EmojiPicker onSelect={onSelect} onClose={() => setOpen(false)} />
                </div>
            ) : null}
        </div>
    );
}
